import { useEffect, useState } from 'react';
import { Pause, Play, SkipForward, Square, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatSeconds } from '@/lib/format';
import { Button } from '@/ui/Button';
import { useSession } from '@/features/auth/session';
import { useTimerControls, useTimerStatus } from './useTimer';

/**
 * Ticks locally between polls; the authoritative elapsed value comes from
 * the server, so a paused timer stays frozen even if this tab keeps running.
 */
export function RunningTimer() {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const { data: status } = useTimerStatus(Boolean(session));
  const { lap, pause, resume, stop } = useTimerControls();
  const [tick, setTick] = useState(0);

  const running = Boolean(status && !status.isPaused);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  if (!status) {
    return (
      <span className="flex items-center gap-2 text-xs text-ink-subtle">
        <Timer className="size-4" aria-hidden />
        {t('timer.noTimer')}
      </span>
    );
  }

  const elapsed = status.isPaused ? status.elapsedSeconds : status.elapsedSeconds + tick;
  const pending = lap.isPending || pause.isPending || resume.isPending || stop.isPending;

  return (
    <div className="flex items-center gap-2">
      <span className={`flex items-center gap-2 rounded-[var(--radius-pill)] px-3 py-1.5 text-xs font-semibold ${
        status.isPaused ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success'}`}>
        <Timer className="size-4" aria-hidden />
        <span className="font-mono tabular-nums">{formatSeconds(elapsed)}</span>
      </span>

      <span className="max-w-44 truncate text-xs text-ink-muted">
        {status.currentItem?.name ?? t('timer.noTask')}
      </span>

      {status.isPaused ? (
        <Button size="sm" variant="outline" loading={pending}
                icon={<Play className="size-3.5" aria-hidden />}
                onClick={() => resume.mutate()}>
          {t('queue.resume')}
        </Button>
      ) : (
        <Button size="sm" variant="ghost" aria-label={t('queue.pause')} loading={pending}
                icon={<Pause className="size-3.5" aria-hidden />}
                onClick={() => pause.mutate()} />
      )}

      <Button size="sm" variant="ghost" aria-label={t('queue.lap')} loading={pending}
              icon={<SkipForward className="size-3.5 rtl:rotate-180" aria-hidden />}
              onClick={() => lap.mutate()} />

      <Button size="sm" variant="outline" loading={pending}
              icon={<Square className="size-3.5" aria-hidden />}
              onClick={() => stop.mutate(undefined)}>
        {t('timer.stop')}
      </Button>
    </div>
  );
}
